import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash, FloppyDisk, UploadSimple, Eye, EyeSlash, ArrowsClockwise, Megaphone } from "@phosphor-icons/react";
import { api, fileUrl, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const FONT_OPTIONS = [
  "Outfit", "Manrope", "Inter", "Bebas Neue",
  "Roboto", "Arial", "Helvetica", "Times New Roman",
  "Courier New", "Georgia", "system-ui",
];

function NumberRow({ label, value, min, max, step = 1, suffix = "", onChange, testId }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase">{label}</label>
        <span className="text-xs font-mono text-neon-cyan tabular-nums">{value}{suffix}</span>
      </div>
      <input
        type="range"
        className="progress w-full"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
      />
    </div>
  );
}

function ColorRow({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <label className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 rounded border border-ink-600 bg-transparent cursor-pointer"
          data-testid={testId}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-24 px-2 py-1 rounded bg-ink-900 border border-ink-600 text-white text-xs font-mono"
        />
      </div>
    </div>
  );
}

function BannerRow({ banner, onChange, onDelete, onUpload }) {
  const [local, setLocal] = useState(banner);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setLocal(banner); }, [banner]);

  const dirty = JSON.stringify(local) !== JSON.stringify(banner);

  const save = async () => {
    setBusy(true);
    try {
      await onChange(local);
      toast.success("Banner saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      await onChange({ ...local, active: !local.active });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-ink-600 bg-ink-900/40 p-4 space-y-3" data-testid={`banner-row-${banner.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {local.image_path && (
            <img src={fileUrl(local.image_path)} alt="" className="w-10 h-10 rounded object-cover border border-ink-600" />
          )}
          <span className={`text-xs font-mono ${local.active ? "text-neon-cyan" : "text-zinc-500"}`}>
            {local.active ? "● ACTIVE" : "○ INACTIVE"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleActive} disabled={busy} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-ink-600 text-zinc-300 hover:text-neon-cyan text-xs transition-colors" data-testid={`banner-toggle-${banner.id}`}>
            {local.active ? <EyeSlash size={12} /> : <Eye size={12} />}
            {local.active ? "Hide" : "Show"}
          </button>
          <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-ink-600 text-zinc-300 hover:text-neon-cyan text-xs transition-colors" data-testid={`banner-upload-${banner.id}`}>
            <UploadSimple size={12} /> Image
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => e.target.files?.[0] && onUpload(banner.id, e.target.files[0])} />
          <button onClick={() => onDelete(banner.id)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-ink-600 text-zinc-300 hover:text-neon-magenta text-xs transition-colors" data-testid={`banner-delete-${banner.id}`}>
            <Trash size={12} />
          </button>
        </div>
      </div>
      <input
        value={local.text}
        onChange={(e) => setLocal({ ...local, text: e.target.value })}
        placeholder="Banner text (e.g., BUDLIGHT × UGSHOOW VOL 02)"
        className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm focus:outline-none focus:border-neon-cyan/60"
        data-testid={`banner-text-${banner.id}`}
      />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input
          value={local.click_url || ""}
          onChange={(e) => setLocal({ ...local, click_url: e.target.value })}
          placeholder="Click-through URL (optional)"
          className="sm:col-span-2 px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-xs font-mono focus:outline-none focus:border-neon-cyan/60"
          data-testid={`banner-url-${banner.id}`}
        />
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={local.text_color || "#ff8a3c"}
            onChange={(e) => setLocal({ ...local, text_color: e.target.value })}
            className="w-9 h-9 rounded border border-ink-600 bg-transparent cursor-pointer"
            data-testid={`banner-color-${banner.id}`}
          />
          <button onClick={save} disabled={busy || !dirty} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-neon-cyan text-black font-semibold text-xs disabled:opacity-50 hover:bg-neon-cyanHover transition-colors" data-testid={`banner-save-${banner.id}`}>
            <FloppyDisk size={12} weight="bold" />
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TickerManager() {
  const [settings, setSettings] = useState(null);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/ticker");
      setSettings(data.settings);
      setBanners(data.banners || []);
    } catch (e) {
      toast.error("Failed to load ticker config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/ticker/settings", settings);
      setSettings(data);
      toast.success("Ticker settings saved — live now");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSavingSettings(false); }
  };

  const addBanner = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/banners", {
        text: "Your promo here",
        click_url: "",
        active: false,
      });
      setBanners((b) => [...b, data]);
      toast.success("Banner draft created");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setCreating(false); }
  };

  const updateBanner = async (banner) => {
    const { data } = await api.put(`/admin/banners/${banner.id}`, {
      text: banner.text,
      click_url: banner.click_url || "",
      text_color: banner.text_color || null,
      active: banner.active,
    });
    setBanners((arr) => arr.map((b) => (b.id === data.id ? data : b)));
  };

  const deleteBanner = async (id) => {
    if (!window.confirm("Delete this banner permanently?")) return;
    try {
      await api.delete(`/admin/banners/${id}`);
      setBanners((arr) => arr.filter((b) => b.id !== id));
      toast.success("Banner deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const uploadBanner = async (id, file) => {
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/banners/${id}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBanners((arr) => arr.map((b) => (b.id === id ? { ...b, image_path: data.path } : b)));
      toast.success("Banner image uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (loading || !settings) {
    return <div className="text-zinc-500 text-sm" data-testid="ticker-manager-loading">Loading ticker manager...</div>;
  }

  const set = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  return (
    <section className="space-y-6" data-testid="ticker-manager">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-1 inline-flex items-center gap-2">
            <Megaphone size={14} weight="fill" /> Ticker & Promo Banners
          </p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">Sell the top banner. Control every pixel.</h2>
          <p className="text-sm text-zinc-500 mt-1">Adjust speed, height, color, and font. Add labels' paid banners that auto-rotate during promo pauses.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan text-xs transition-colors" data-testid="ticker-refresh">
          <ArrowsClockwise size={12} /> Reload
        </button>
      </div>

      {/* Settings */}
      <div className="rounded-lg border border-ink-600 bg-ink-800/40 p-5 lg:p-6 space-y-5" data-testid="ticker-settings-panel">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-8">
          <div className="space-y-4">
            <NumberRow label="Scroll duration" value={settings.scroll_duration_ms} min={5000} max={120000} step={500} suffix=" ms" onChange={(v) => set("scroll_duration_ms", v)} testId="ticker-scroll-duration" />
            <NumberRow label="Promo banner duration" value={settings.promo_duration_ms} min={1000} max={20000} step={250} suffix=" ms" onChange={(v) => set("promo_duration_ms", v)} testId="ticker-promo-duration" />
            <NumberRow label="Ticker height" value={settings.height_px} min={28} max={120} suffix=" px" onChange={(v) => set("height_px", v)} testId="ticker-height" />
            <NumberRow label="Background opacity" value={settings.background_opacity} min={0} max={100} suffix=" %" onChange={(v) => set("background_opacity", v)} testId="ticker-bg-opacity" />
            <NumberRow label="Letter spacing" value={settings.letter_spacing_em} min={0} max={0.6} step={0.02} suffix=" em" onChange={(v) => set("letter_spacing_em", v)} testId="ticker-letter-spacing" />
            <NumberRow label="Font weight" value={settings.font_weight} min={300} max={900} step={100} onChange={(v) => set("font_weight", v)} testId="ticker-font-weight" />
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5 block">Font family</label>
              <select
                value={settings.font_family}
                onChange={(e) => set("font_family", e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm focus:outline-none focus:border-neon-cyan/60"
                data-testid="ticker-font-family"
              >
                {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <ColorRow label="Artist text color" value={settings.scroll_text_color} onChange={(v) => set("scroll_text_color", v)} testId="ticker-scroll-color" />
            <ColorRow label="Vote CTA color" value={settings.cta_text_color} onChange={(v) => set("cta_text_color", v)} testId="ticker-cta-color" />
            <ColorRow label="Promo banner color" value={settings.promo_text_color} onChange={(v) => set("promo_text_color", v)} testId="ticker-promo-color" />
            <div className="flex items-center justify-between pt-1">
              <label className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase">Ticker enabled</label>
              <button
                onClick={() => set("enabled", !settings.enabled)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${settings.enabled ? "bg-neon-cyan text-black" : "border border-ink-600 text-zinc-400"}`}
                data-testid="ticker-enabled-toggle"
              >
                {settings.enabled ? "ON" : "OFF"}
              </button>
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div className="space-y-2">
          <p className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase">Live preview</p>
          <div
            className="rounded-md border border-neon-cyan/20 overflow-hidden flex items-center justify-center"
            style={{
              minHeight: `${settings.height_px}px`,
              backgroundColor: `rgba(12, 9, 7, ${(settings.background_opacity ?? 85) / 100})`,
            }}
            data-testid="ticker-preview"
          >
            <span
              className="px-6 uppercase"
              style={{
                fontFamily: settings.font_family,
                fontWeight: settings.font_weight,
                letterSpacing: `${settings.letter_spacing_em}em`,
                color: settings.scroll_text_color,
                fontSize: "12px",
              }}
            >
              ● Almight E&nbsp;&nbsp;
              <span style={{ color: settings.cta_text_color }}>★ Vote for your favorite song</span>
              &nbsp;&nbsp;● Zilla1900&nbsp;&nbsp;
              <span style={{ color: settings.promo_text_color }}>📣 Your promo here</span>
            </span>
          </div>
        </div>

        <div className="flex items-center justify-end">
          <button
            onClick={saveSettings}
            disabled={savingSettings}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-neon-cyan text-black font-semibold text-sm hover:bg-neon-cyanHover transition-colors disabled:opacity-60"
            data-testid="ticker-save-settings"
          >
            <FloppyDisk size={14} weight="bold" />
            {savingSettings ? "Saving..." : "Save ticker settings"}
          </button>
        </div>
      </div>

      {/* Banners list */}
      <div className="rounded-lg border border-ink-600 bg-ink-800/40 p-5 lg:p-6 space-y-4" data-testid="ticker-banners-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-bold tracking-tight">Promo banners</h3>
            <p className="text-xs text-zinc-500">Active banners rotate during the promo pause. Inactive banners stay hidden.</p>
          </div>
          <button
            onClick={addBanner}
            disabled={creating}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-neon-cyan/40 text-neon-cyan hover:bg-neon-cyan hover:text-black text-xs font-semibold transition-colors disabled:opacity-60"
            data-testid="ticker-add-banner"
          >
            <Plus size={14} weight="bold" /> New banner
          </button>
        </div>
        {banners.length === 0 ? (
          <p className="text-sm text-zinc-500 py-6 text-center">No banners yet. Add one to start selling promo slots.</p>
        ) : (
          <div className="space-y-3">
            {banners.map((b) => (
              <BannerRow key={b.id} banner={b} onChange={updateBanner} onDelete={deleteBanner} onUpload={uploadBanner} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
